<script lang="ts" setup>
import { computed, onMounted, ref, toRefs, watch } from "vue";
import { UserLocaleCode } from "@/shared/@types/UserLocaleCode";
import { FRIENDLY_CAPTCHA_PUZZLE_ENDPOINT } from "@/shared/helper/constants.helper";
import { isDevelopment } from "@/shared/helper/environment.helper";
import { WidgetInstance, localizations } from "friendly-challenge";
import { i18n } from "@/shared/i18n/i18n";
import { onUnmounted } from "vue";

const SITEKEY = import.meta.env.VITE_FRIENDLY_CAPTCHA_SITEKEY;

const props = defineProps<{
  modelValue?: string;
}>();

const { modelValue } = toRefs(props);

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();

const locale = i18n.t("shared.term.localeString").split("-")[0];
const widgetLocale =
  // prettier-ignore
  (
    locale && locale in Object.keys(localizations) ? locale : UserLocaleCode.DE
  ) as keyof typeof localizations;

const widgetElement = ref<HTMLElement | null>(null);
const widget = ref<WidgetInstance>();
const captchaResult = ref(modelValue.value || "");

const captchaResultComputed = computed({
  get: () => captchaResult.value,
  set: (value: string) => {
    captchaResult.value = value;
    emit("update:modelValue", value);
  },
});

const doneCallback = (solution: string) => {
  captchaResultComputed.value = solution;
};

const errorCallback = () => {
  captchaResultComputed.value = "ERROR";
};

const startCaptchaValidation = () => {
  if (!widgetElement.value) return;

  // Skip captcha in development/test environment
  if (isDevelopment) {
    setTimeout(() => {
      captchaResultComputed.value = "test";
      widgetElement.value?.setAttribute("data-testid", "captcha-mock");
    }, 100);
    return;
  }

  widget.value = new WidgetInstance(widgetElement.value, {
    doneCallback,
    errorCallback,
    sitekey: SITEKEY,
    puzzleEndpoint: FRIENDLY_CAPTCHA_PUZZLE_ENDPOINT,
    language: widgetLocale,
  });

  widget.value.start();
};

watch(modelValue, (newValue) => {
  if (!newValue && widget.value) {
    widget.value?.reset();
    startCaptchaValidation();
  }
});

onMounted(() => {
  startCaptchaValidation();
});

onUnmounted(() => {
  if (widget.value) {
    widget.value.destroy();
  }
});
</script>

<template>
  <div
    ref="widgetElement"
    class="frc-captcha"
  ></div>
</template>
